import axios from 'axios'
import hash from 'object-hash'

import {
  CompassAPIMultiSeriesChartData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
} from '../utils/compass'
import { capitalizeString, formatSeriesNames } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

export namespace CarRental {
  export namespace AlugueisEAssinaturas {
    type AlugueisEAssinaturasChartId = 'prices_state_sipp' | 'prices_car_name'

    const ALUGUEIS_E_ASSINATURAS_CHART_PATHS: Record<
      AlugueisEAssinaturasChartId,
      string
    > = {
      prices_state_sipp: '/alugueis-e-assinaturas/prices-state-sipp',
      prices_car_name: '/alugueis-e-assinaturas/prices-car-name',
    }

    const CHART_TITLES: Record<AlugueisEAssinaturasChartId, string> = {
      prices_state_sipp: 'Preços Aluguel',
      prices_car_name: 'Preços Assinaturas',
    }

    const CONTEXT_ID = 'equity:car_rental:alugueis_e_assinaturas'
    const CAR_RENTAL_CHARTS_PATH = '/api/charts/car-rental'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: AlugueisEAssinaturasChartId
      state?: string
      sipp?: string
      carName?: string
      title?: string
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          CAR_RENTAL_CHARTS_PATH +
          ALUGUEIS_E_ASSINATURAS_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          ...(config.state ? { state: config.state } : {}),
          ...(config.sipp ? { sipp: config.sipp } : {}),
          ...(config.carName ? { car_name: config.carName } : {}),
        },
      }).then((response) => response.data)

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          chartTitle: `${CHART_TITLES[config.chart]} ${
            config.carName
              ? `(${
                  config.carName &&
                  capitalizeString(config.carName.replaceAll('_', ' '))
                })`
              : `(${config.state} - ${config.sipp})`
          }`,
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, formatSeriesNames(data))

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:car_rental:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'pe_rent3_bz' | 'pe_movi3_bz' | 'pe_lcam3_bz' | 'pe_vamo3_bz'
      context: typeof CONTEXT_ID
      title: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          chartTitle: config.title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        })(config, data),
    }
  }
}
